import { useState, useRef, useEffect } from 'react';
import { TablePagination } from '../components/TablePagination';
import { Card, Group, Stack, Text, Badge, TextInput, Button, ScrollArea, Loader, Center, SegmentedControl, Switch, useMantineTheme } from '@mantine/core';
import { IconSend, IconMessageCircle, IconCrown, IconArrowLeft } from '@tabler/icons-react';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { TableToolbar } from '../components/TableToolbar';
import { EmptyState } from '../components/EmptyState';
import { FetchOverlay } from '../components/FetchOverlay';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { formatPhone } from '../lib/format';
import { genderRoleLabel } from '../lib/roleLabel';
import { PREMIUM_UI_ENABLED } from '../lib/features';

interface ThreadUser { id: string; fullName: string; phone?: string | null; premiumUntil?: string | null; role?: string; gender?: 'MALE' | 'FEMALE' | null }
interface Thread { user: ThreadUser | null; lastMessage: { body: string; createdAt: string; fromSupport: boolean } | null; unread: number; totalMessages: number }
interface Msg { id: string; fromSupport: boolean; body: string; createdAt: string }

/**
 * Premium belgisi uchun tekshiruv.
 * MUZLATILGAN (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`): hozir barcha
 * ota-onada premium bor, ya'ni toj (crown) HAMMADA chiqib, hech narsani
 * ajratmasdi — shu sababli belgi butunlay yashirilgan.
 */
function isPremium(u?: ThreadUser | null) {
  return PREMIUM_UI_ENABLED && !!u?.premiumUntil && new Date(u.premiumUntil) > new Date();
}

/** Rol + jins bo'yicha badge (Ota/Ona/Ustoz/O'qituvchi) — yagona helper. */
const roleBadge = (u?: ThreadUser | null) => genderRoleLabel(u?.role, u?.gender);

export default function Support() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  /* TELEFONDA ro'yxat va suhbat YONMA-YON sig'maydi (340px ro'yxat + suhbat):
     suhbat ekrandan tashqariga chiqib ketardi. Telefonda bittasi ko'rinadi —
     suhbat tanlansa ro'yxat yashirinadi, "Orqaga" bilan qaytiladi. */
  const theme = useMantineTheme();
  const telefon = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const [text, setText] = useState('');
  const viewportRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [roleF, setRoleF] = useState<'all' | 'parent' | 'teacher'>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { items: threads, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Thread>('support-threads', '/support', {
    q: qDeb || undefined,
    role: roleF === 'all' ? undefined : roleF,
    unread: unreadOnly ? '1' : undefined,
  });
  // Filtrlangan bo'sh natija ma'lumot umuman yo'qligidan farqli matn ko'rsatadi
  const filterActive = !!qDeb || roleF !== 'all' || unreadOnly;

  const { data: thread } = useQuery({
    queryKey: ['support-thread', activeId],
    queryFn: async () => (await api.get<{ user: ThreadUser; messages: Msg[] }>(`/support/${activeId}`)).data,
    enabled: !!activeId,
    refetchInterval: 15_000,
  });

  // Thread ochilib xabarlar yuklangach (server ularni o'qilgan deb belgilaydi) —
  // thread ro'yxatini yangilaymiz, o'qilmaganlar badge'i darhol yo'qolsin.
  useEffect(() => {
    if (activeId && thread?.user?.id === activeId) {
      qc.invalidateQueries({ queryKey: ['support-threads'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, thread?.user?.id, thread?.messages?.length]);

  const replyMut = useMutation({
    mutationFn: (body: string) => api.post(`/support/${activeId}`, { body }),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['support-thread', activeId] });
      qc.invalidateQueries({ queryKey: ['support-threads'] });
    },
  });

  useEffect(() => {
    viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight });
  }, [thread?.messages?.length]);

  return (
    <>
      <PageHeader title={t('supportPage.title')} />
      {/* Saralash BERILMAYDI — threadlar doim oxirgi xabar bo'yicha tartiblanadi (server) */}
      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('supportPage.searchPlaceholder')}
      >
        <SegmentedControl
          value={roleF}
          onChange={(v) => { setRoleF(v as 'all' | 'parent' | 'teacher'); setPage(1); }}
          data={[
            { value: 'all', label: t('teachersPage.allRoles') },
            { value: 'parent', label: t('nav.parents') },
            { value: 'teacher', label: t('nav.teachers') },
          ]}
        />
        <Switch
          mb={8}
          label={t('supportPage.unreadOnly')}
          checked={unreadOnly}
          onChange={(e) => { setUnreadOnly(e.currentTarget.checked); setPage(1); }}
        />
      </TableToolbar>
      <Group align="flex-start" gap="md" wrap="nowrap">
        {/* Threadlar */}
        {!(telefon && activeId) && (
        <Stack w={{ base: '100%', sm: 340 }} gap="xs" style={{ flexShrink: 0 }}>
          {/* Threadlar ro'yxati ham sarlavhali kartada — suhbat paneli bilan bir xil */}
          <SectionCard title={t('common.list')} icon={IconMessageCircle} p="sm" pos="relative">
            <FetchOverlay visible={fetching} />
            {isLoading ? (
              <Center py="xl"><Loader /></Center>
            ) : !threads.length ? (
              <EmptyState
                icon={IconMessageCircle}
                title={filterActive ? t('supportPage.noMatch') : t('supportPage.noMessages')}
                description={filterActive ? t('supportPage.noMatchDesc') : t('supportPage.emptyDesc')}
              />
            ) : (
              <Stack gap="xs">
                {threads.map((th) => (
                  <Card
                    key={th.user?.id}
                    p="sm"
                    withBorder={activeId === th.user?.id}
                    style={{ cursor: 'pointer', borderColor: activeId === th.user?.id ? 'var(--mantine-color-brand-5)' : undefined }}
                    onClick={() => th.user && setActiveId(th.user.id)}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <div style={{ minWidth: 0 }}>
                        <Group gap={6} wrap="nowrap">
                          <Text fw={600} size="sm" truncate>{th.user?.fullName}</Text>
                          {roleBadge(th.user) && <Badge size="xs" variant="light" color={roleBadge(th.user)!.color}>{roleBadge(th.user)!.label}</Badge>}
                          {isPremium(th.user) && <IconCrown size={14} color="var(--mantine-color-yellow-6)" />}
                        </Group>
                        <Text size="xs" c="dimmed" truncate>
                          {th.lastMessage ? (th.lastMessage.fromSupport ? t('supportPage.youPrefix') : '') + th.lastMessage.body : ''}
                        </Text>
                      </div>
                      {th.unread > 0 && <Badge size="sm" circle color="red">{th.unread}</Badge>}
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </SectionCard>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPage={setPage}
            onLimit={setLimit}
          />
        </Stack>
        )}

        {/* Suhbat — sarlavha boshqa kartalardagi kabi header'da (tanlanmaganda ham ko'rinadi) */}
        {!(telefon && !activeId) && (
        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
        {telefon && (
          <Button variant="subtle" size="compact-sm" leftSection={<IconArrowLeft size={14} />} onClick={() => setActiveId(null)} w="fit-content">
            {t('supportPage.back')}
          </Button>
        )}
        <SectionCard
          title={activeId ? (thread?.user?.fullName ?? '') : t('supportPage.title')}
          icon={IconMessageCircle}
          subtitle={activeId ? formatPhone(thread?.user?.phone) : undefined}
          right={activeId ? (
            <Group gap="xs" wrap="nowrap">
              {roleBadge(thread?.user) && <Badge variant="light" color={roleBadge(thread?.user)!.color}>{roleBadge(thread?.user)!.label}</Badge>}
              {isPremium(thread?.user) && <Badge color="yellow" leftSection={<IconCrown size={12} />}>{t('nav.premium')}</Badge>}
            </Group>
          ) : undefined}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 480 }}
          bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          {!activeId ? (
            <Center style={{ flex: 1 }}><Text c="dimmed">{t('supportPage.selectThread')}</Text></Center>
          ) : (
            <>
              <ScrollArea style={{ flex: 1 }} viewportRef={viewportRef} mb="sm">
                <Stack gap="xs" p="xs">
                  {(thread?.messages ?? []).map((m) => (
                    <Card
                      key={m.id}
                      p="xs"
                      maw="75%"
                      style={{
                        alignSelf: m.fromSupport ? 'flex-end' : 'flex-start',
                        background: m.fromSupport ? 'var(--mantine-color-brand-1)' : 'var(--mantine-color-gray-1)',
                      }}
                    >
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{m.body}</Text>
                      <Text size="xs" c="dimmed" ta="right">{new Date(m.createdAt).toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>
                    </Card>
                  ))}
                </Stack>
              </ScrollArea>
              <Group gap="xs">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder={t('supportPage.replyPlaceholder')}
                  value={text}
                  onChange={(e) => setText(e.currentTarget.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) replyMut.mutate(text.trim()); }}
                />
                <Button leftSection={<IconSend size={16} />} loading={replyMut.isPending} disabled={!text.trim()} onClick={() => replyMut.mutate(text.trim())}>
                  {t('staffPage.send')}
                </Button>
              </Group>
            </>
          )}
        </SectionCard>
        </Stack>
        )}
      </Group>
    </>
  );
}
